<template>
    <div
        v-if="kindLabel"
        dusk="AddOnAnchorBlock"
        class="rounded-lg border border-theme-accent-ring bg-theme-accent-bg px-3 py-2.5"
    >
        <div class="flex items-center gap-2">
            <div role="group" :aria-label="strings.anchorKinds" class="flex items-center gap-1">
                <button
                    v-for="option in kindOptions"
                    :key="option.kind"
                    type="button"
                    :dusk="`AddOnAnchorKind-${option.kind}`"
                    :aria-label="option.label"
                    :title="option.label"
                    :aria-pressed="String(option.chosen)"
                    :data-autofocus="returnFocus && option.chosen && !recaptureLabel ? '' : null"
                    class="inline-flex h-11 w-11 items-center justify-center rounded-md border border-theme-accent-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2 md:h-8 md:w-8"
                    :class="
                        option.chosen
                            ? 'bg-theme-accent text-theme-accent-on'
                            : 'bg-theme-bg text-theme-accent hover:bg-theme-accent-bg'
                    "
                    @click="setAddOnEditorAnchorKind(option.kind)"
                    @blur="returnFocus = false"
                >
                    <svg-icon :icon="option.icon" width="16px" height="16px" />
                </button>
            </div>
            <p
                dusk="AddOnAnchorKind"
                class="m-0 text-[10.5px] font-semibold uppercase tracking-wider text-theme-accent"
            >
                {{ kindLabel }}
            </p>
        </div>
        <p v-if="description" dusk="AddOnAnchorDescription" class="m-0 mt-1.5 text-[12px] text-theme-text">
            {{ description }}
        </p>
        <button
            v-if="recaptureLabel"
            type="button"
            dusk="AddOnAnchorRecapture"
            :data-autofocus="returnFocus ? '' : null"
            class="mt-2 inline-flex min-h-11 items-center rounded-md border border-theme-accent-ring bg-theme-bg px-2 py-1 text-[11.5px] font-medium text-theme-accent hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent md:min-h-0"
            @click="recaptureAddOnEditorAnchor()"
            @blur="returnFocus = false"
        >
            {{ recaptureLabel }}
        </button>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import { addOnStrings } from './addOnStrings.js';
import { anchorPageNumber } from './addOnAnchor.js';
import { ANCHOR_KINDS, OFFERED_ANCHOR_KINDS, anchorKindKeys } from './addOnAnchorKinds.js';

export default {
    name: 'AddOnAnchorBlock',
    props: {
        // Null for a record the host left unplaced, which an editor still opens.
        anchor: { type: Object, default: null },
    },
    data() {
        return { returnFocus: false };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['canPlacePoint', 'addOnEditorAwaitsAnchor']),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        keys() {
            return anchorKindKeys(this.anchor?.type);
        },
        // The whole block hangs on this: naming the wrong place would tell an
        // operator the piece sits somewhere it does not.
        kindLabel() {
            return (this.keys && this.strings[this.keys.kind]) || '';
        },
        kindOptions() {
            return OFFERED_ANCHOR_KINDS.filter(kind => kind !== 'point' || this.canPlacePoint).map(kind => ({
                kind,
                icon: ANCHOR_KINDS[kind].icon,
                label: this.strings[ANCHOR_KINDS[kind].kind] || kind,
                chosen: this.anchor?.type === kind,
            }));
        },
        description() {
            if (!this.keys) return '';
            // A pending anchor's page is where the operator stands, not where
            // the piece will sit, so nothing may be said about it yet.
            if (this.addOnEditorAwaitsAnchor) return this.strings[this.keys.awaiting] || '';

            const template = this.strings[this.keys.anchored];
            if (!template) return '';
            if (!template.includes('{page}')) return template;

            const page = anchorPageNumber(this.anchor.page);
            return page === null ? '' : template.replace('{page}', String(page));
        },
        // Only a passage and a point are the operator's to point at, and one
        // the page is already being asked for has nothing to ask again.
        recaptureLabel() {
            if (this.addOnEditorAwaitsAnchor || !this.keys?.recapture) return '';

            return this.strings[this.keys.recapture] || '';
        },
    },
    watch: {
        // The panel's focus trap comes back when the capture ends and focuses
        // whatever carries data-autofocus.
        addOnEditorAwaitsAnchor(awaits, awaited) {
            if (awaited && !awaits) this.returnFocus = true;
        },
    },
    methods: {
        ...mapActions(['setAddOnEditorAnchorKind', 'recaptureAddOnEditorAnchor']),
    },
};
</script>
